import Phaser from 'phaser';
import { COLORS, DEPTHS, FIELD_GUIDE, FONTS, GAME_HEIGHT, GAME_WIDTH, SCORECARD, toHexColor } from '../config/gameConfig';
import { COPY } from '../content/copy';
import { TEACH_PANELS } from '../content/teach-panels';
import { FIELD_GUIDE_CONTEXT } from '../content/field-guide-context';
import { VOCAB_QUIZ } from '../content/vocab-quiz';
import type { Email, Term } from '../systems/EventBus';
import type { UnlockRecord, Scorecard } from '../systems/SessionTracker';
import { AudioManager } from '../systems/AudioManager';
import { formatFictionalTime } from '../systems/Clock';
import {
  buildShareData,
  copyShareText,
  downloadShareCardImage,
  renderShareText,
} from '../systems/ShareCard';
import { sendAnalytics, generateRunId } from '../systems/Analytics';

interface EndingData {
  realElapsedMs?: number;
  emails?: readonly Email[];
  termUnlocks?: ReadonlyMap<Term, UnlockRecord>;
  scorecard?: Readonly<Scorecard>;
}

// Beat offsets (ms from scene start).
const BEAT1_START = 0;
const BEAT1_DURATION = 3000;
const BEAT2_START = BEAT1_START + BEAT1_DURATION;
const BEAT2_TYPE_DURATION = 2000;
const BEAT2_HOLD = 3000;
const BEAT2_DURATION = BEAT2_TYPE_DURATION + BEAT2_HOLD;
const BEAT3_START = BEAT2_START + BEAT2_DURATION;
const BEAT3_SCROLL_DURATION = 8000;
const BEAT3_LINGER = 4000;
const BEAT3_DURATION = BEAT3_SCROLL_DURATION + BEAT3_LINGER;
const BEAT4_START = BEAT3_START + BEAT3_DURATION;
const BEAT4_DURATION = 24_000;
const BEAT5_START = BEAT4_START + BEAT4_DURATION;
const BEAT5_DURATION = 15_000;
const BEAT6_START = BEAT5_START + BEAT5_DURATION;
const BEAT6_LINE1_DURATION = 600;
const BEAT6_LINE2_DELAY = 2000;
const BEAT6_LINE2_DURATION = 600;
const BEAT6_BUTTON_DELAY = 3600;
const BEAT6_BUTTON_DURATION = 400;

const CORE_TERM_ORDER: readonly Term[] = [
  'aging-bucket',
  'dunning',
  'cash-application',
  'short-pay',
  'dispute',
  'promise-to-pay',
  'write-off',
  'dso',
];

interface ScorecardRow {
  label: string;
  value: string;
  ok?: 'yes' | 'no' | 'none';
  okCaption?: string;
  okDetail?: string;
}

export class EndingScene extends Phaser.Scene {
  private elapsedMs = 0;
  private emails: readonly Email[] = [];
  private termUnlocks: ReadonlyMap<Term, UnlockRecord> = new Map();
  private quizScore: number | null = null;
  private scorecard: Readonly<Scorecard> = {
    dunningSent: 0,
    dunningRightBucket: 0,
    cashApplicationsSent: 0,
    cashAppliedUsd: 0,
    cashCorrectCustomer: 0,
    shortPaysResolved: 0,
    disputesLogged: 0,
    unappliedAtCloseUsd: 0,
    promiseToPaySolid: 0,
    promiseToPaySoft: 0,
    writeOffsApproved: 0,
    writeOffsDeclined: 0,
    dsoAtClose: null,
    paymentsExpired: 0,
    emailsActionedCorrectly: 0,
    emailsActionedWrong: 0,
    liveScore: 0,
  };

  constructor() {
    super({ key: 'EndingScene' });
  }

  create(data: EndingData): void {
    this.elapsedMs = data?.realElapsedMs ?? 0;
    this.emails = data?.emails ?? [];
    this.termUnlocks = data?.termUnlocks ?? new Map();
    if (data?.scorecard) this.scorecard = data.scorecard;

    // Persistent black backdrop under every beat.
    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.black).setOrigin(0, 0).setDepth(DEPTHS.ending);

    this.time.delayedCall(BEAT1_START, () => this.renderBeat1());
    this.time.delayedCall(BEAT2_START, () => this.renderBeat2());
    this.time.delayedCall(BEAT3_START, () => this.renderBeat3());
    this.time.delayedCall(BEAT4_START, () => this.renderBeat4());
    this.time.delayedCall(BEAT5_START, () => this.renderBeat5());
  }

  /** Beat 1: 5pm pause — dim screen slightly, phone rings once, silence. */
  private renderBeat1(): void {
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.black, 0.3).setOrigin(0, 0).setDepth(DEPTHS.ending + 1);
    this.time.delayedCall(500, () => AudioManager.getInstance().playPhoneRing());
    this.time.delayedCall(BEAT1_DURATION, () => dim.destroy());
  }

  /** Beat 2: Jamie's parting Slack line types out, holds, fades. */
  private renderBeat2(): void {
    const text = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
      fontFamily: FONTS.ui,
      fontSize: '20px',
      color: '#ffffff',
      wordWrap: { width: GAME_WIDTH - 200 },
      align: 'center',
    }).setOrigin(0.5, 0.5).setDepth(DEPTHS.ending + 2);

    const fullText = COPY.ending.beat2Slack;
    const charInterval = BEAT2_TYPE_DURATION / fullText.length;
    let shown = 0;
    const timer = this.time.addEvent({
      delay: charInterval,
      repeat: fullText.length - 1,
      callback: () => {
        shown += 1;
        text.setText(fullText.slice(0, shown));
      },
    });

    this.time.delayedCall(BEAT2_DURATION, () => {
      timer.remove();
      this.tweens.add({ targets: text, alpha: 0, duration: 400, onComplete: () => text.destroy() });
    });
  }

  /** Beat 3: the day's accumulated emails scroll up past the viewport. */
  private renderBeat3(): void {
    const header = this.add.text(GAME_WIDTH / 2, 80, COPY.ending.beat3Header, {
      fontFamily: FONTS.ui,
      fontSize: '18px',
      color: toHexColor(COLORS.textMuted),
    }).setOrigin(0.5, 0).setAlpha(0).setDepth(DEPTHS.ending + 3);
    this.tweens.add({ targets: header, alpha: 1, duration: 600 });

    const listContainer = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT + 20).setDepth(DEPTHS.ending + 3);
    this.emails.forEach((email, i) => {
      const line = this.add.text(0, i * 24, `• ${email.subject}`, {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(COLORS.emailSubject),
      }).setOrigin(0.5, 0);
      listContainer.add(line);
    });

    const totalHeight = this.emails.length * 24;
    const travel = totalHeight + (GAME_HEIGHT - 120);
    this.tweens.add({
      targets: listContainer,
      y: `-=${travel}`,
      duration: BEAT3_SCROLL_DURATION,
      ease: 'Linear',
    });

    this.time.delayedCall(BEAT3_DURATION - 500, () => {
      this.tweens.add({ targets: [header, listContainer], alpha: 0, duration: 500, onComplete: () => {
        header.destroy();
        listContainer.destroy();
      } });
    });
  }

  /** Beat 4: Field Guide — cycles all 8 core terms with unlock timestamps. */
  private renderBeat4(): void {
    let cancelled = false;
    const cards: Phaser.GameObjects.Container[] = [];
    const scheduled: Phaser.Time.TimerEvent[] = [];

    const skipHint = this.add.text(
      FIELD_GUIDE.skipHintX,
      FIELD_GUIDE.skipHintY,
      COPY.ending.beat4SkipHint,
      { fontFamily: FONTS.ui, fontSize: '12px', color: toHexColor(COLORS.textMuted) },
    ).setOrigin(1, 0.5).setAlpha(0.6).setDepth(DEPTHS.ending + 5);

    const cleanup = (): void => {
      cancelled = true;
      scheduled.forEach((t) => t.remove());
      cards.forEach((c) => c.destroy());
      skipHint.destroy();
    };

    const skipHandler = (): void => {
      if (cancelled) return;
      this.input.off('pointerdown', skipHandler);
      cleanup();
      this.renderBeat5();
    };
    this.input.on('pointerdown', skipHandler);

    CORE_TERM_ORDER.forEach((term, i) => {
      const cardStart = i * FIELD_GUIDE.perCardTotal;
      scheduled.push(this.time.delayedCall(cardStart, () => {
        if (cancelled) return;
        const card = this.buildFieldGuideCard(term);
        cards.push(card);
        this.tweens.add({ targets: card, alpha: 1, duration: FIELD_GUIDE.cardFadeIn });
        this.time.delayedCall(FIELD_GUIDE.cardFadeIn + FIELD_GUIDE.cardHold, () => {
          if (cancelled) return;
          this.tweens.add({
            targets: card,
            alpha: 0,
            duration: FIELD_GUIDE.cardFadeOut,
            onComplete: () => card.destroy(),
          });
        });
      }));
    });

    scheduled.push(this.time.delayedCall(CORE_TERM_ORDER.length * FIELD_GUIDE.perCardTotal, () => {
      if (cancelled) return;
      this.input.off('pointerdown', skipHandler);
      skipHint.destroy();
    }));
  }

  private buildFieldGuideCard(term: Term): Phaser.GameObjects.Container {
    const container = this.add.container(GAME_WIDTH / 2, FIELD_GUIDE.cardY)
      .setAlpha(0)
      .setDepth(DEPTHS.ending + 4);

    const unlock = this.termUnlocks.get(term);
    const panel = TEACH_PANELS[term];

    if (unlock) {
      container.add(this.add.text(0, -80, panel.title, {
        fontFamily: FONTS.ui,
        fontSize: `${FIELD_GUIDE.titleSize}px`,
        color: '#ffffff',
        fontStyle: 'bold',
      }).setOrigin(0.5));

      container.add(this.add.text(0, -20, panel.definition, {
        fontFamily: FONTS.ui,
        fontSize: `${FIELD_GUIDE.definitionSize}px`,
        color: '#ffffff',
        wordWrap: { width: FIELD_GUIDE.cardWidth - 40 },
        align: 'center',
      }).setOrigin(0.5, 0));

      container.add(this.add.text(0, 50, panel.empathyLine, {
        fontFamily: FONTS.ui,
        fontSize: `${FIELD_GUIDE.empathySize}px`,
        color: toHexColor(COLORS.textMuted),
        fontStyle: 'italic',
        wordWrap: { width: FIELD_GUIDE.cardWidth - 40 },
        align: 'center',
      }).setOrigin(0.5, 0));

      const time = formatFictionalTime(unlock.fictionalMinutes);
      const hook = unlock.beatId ? FIELD_GUIDE_CONTEXT[unlock.beatId] : undefined;
      const caption = hook
        ? COPY.ending.beat4CaptionFormat.replace('{time}', time).replace('{hook}', hook)
        : COPY.ending.beat4CaptionNoHook.replace('{time}', time);

      container.add(this.add.text(0, FIELD_GUIDE.cardHeight / 2 - FIELD_GUIDE.captionSize - 10, caption, {
        fontFamily: FONTS.ui,
        fontSize: `${FIELD_GUIDE.captionSize}px`,
        color: toHexColor(COLORS.textMuted),
        wordWrap: { width: FIELD_GUIDE.cardWidth - 40 },
        align: 'center',
      }).setOrigin(0.5, 0));
    } else {
      container.add(this.add.text(0, -30, COPY.ending.beat4LockedTitle(panel.title), {
        fontFamily: FONTS.ui,
        fontSize: `${FIELD_GUIDE.titleSize}px`,
        color: toHexColor(COLORS.textMuted),
      }).setOrigin(0.5));

      container.add(this.add.text(0, 20, COPY.ending.beat4LockedBody, {
        fontFamily: FONTS.ui,
        fontSize: `${FIELD_GUIDE.definitionSize}px`,
        color: toHexColor(COLORS.textMuted),
        align: 'center',
      }).setOrigin(0.5));
    }

    return container;
  }

  /** Beat 5: Craft scorecard — aggregated session stats. */
  private renderBeat5(): void {
    let advanced = false;
    const objects: Phaser.GameObjects.GameObject[] = [];
    const scheduled: Phaser.Time.TimerEvent[] = [];

    const advance = (): void => {
      if (advanced) return;
      advanced = true;
      this.input.off('pointerdown', advance);
      scheduled.forEach((t) => t.remove());
      this.tweens.add({
        targets: objects,
        alpha: 0,
        duration: 400,
        onComplete: () => {
          objects.forEach((o) => o.destroy());
          this.renderBeat6();
        },
      });
    };

    const title = this.add.text(GAME_WIDTH / 2, SCORECARD.titleY, COPY.ending.beat5Title, {
      fontFamily: FONTS.ui,
      fontSize: `${SCORECARD.titleSize}px`,
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5).setAlpha(0).setDepth(DEPTHS.ending + 4);
    objects.push(title);
    this.tweens.add({ targets: title, alpha: 1, duration: SCORECARD.rowFadeIn });

    const rows = this.buildScorecardRows();
    rows.forEach((row, i) => {
      const rowObjects = this.renderScorecardRow(row, SCORECARD.rowStartY + i * SCORECARD.rowGap);
      rowObjects.forEach((o) => {
        o.setAlpha(0).setDepth(DEPTHS.ending + 4);
        objects.push(o);
      });
      scheduled.push(this.time.delayedCall((i + 1) * SCORECARD.rowStagger, () => {
        if (advanced) return;
        this.tweens.add({ targets: rowObjects, alpha: 1, duration: SCORECARD.rowFadeIn });
      }));
    });

    const closing = this.add.text(GAME_WIDTH / 2, SCORECARD.closingY, COPY.ending.beat5Closing, {
      fontFamily: FONTS.ui,
      fontSize: `${SCORECARD.closingSize}px`,
      color: toHexColor(COLORS.textMuted),
      fontStyle: 'italic',
    }).setOrigin(0.5).setAlpha(0).setDepth(DEPTHS.ending + 4);
    objects.push(closing);

    scheduled.push(this.time.delayedCall(SCORECARD.closingDelay, () => {
      if (advanced) return;
      this.tweens.add({ targets: closing, alpha: 1, duration: SCORECARD.rowFadeIn });
      this.input.on('pointerdown', advance);
    }));

    scheduled.push(this.time.delayedCall(SCORECARD.autoAdvanceMs, advance));
  }

  private buildScorecardRows(): ScorecardRow[] {
    const s = this.scorecard;
    const rows: ScorecardRow[] = [];

    rows.push({
      label: COPY.ending.beat5Rows.dunningSent,
      value: String(s.dunningSent),
      ok: s.dunningSent === 0 ? 'none'
        : s.dunningRightBucket === s.dunningSent ? 'yes' : 'no',
      okCaption: COPY.ending.beat5Rows.dunningBucketOk,
      okDetail: s.dunningSent === 0 ? undefined : `${s.dunningRightBucket} / ${s.dunningSent}`,
    });

    rows.push({
      label: COPY.ending.beat5Rows.cashApplied,
      value: `$${s.cashAppliedUsd.toLocaleString()}`,
      ok: s.cashApplicationsSent === 0 ? 'none'
        : s.cashCorrectCustomer === s.cashApplicationsSent ? 'yes' : 'no',
      okCaption: COPY.ending.beat5Rows.cashCustomerOk,
      okDetail: s.cashApplicationsSent === 0 ? undefined
        : `${s.cashCorrectCustomer} / ${s.cashApplicationsSent}`,
    });

    rows.push({
      label: COPY.ending.beat5Rows.shortPays,
      value: String(s.shortPaysResolved),
    });

    rows.push({
      label: COPY.ending.beat5Rows.disputes,
      value: String(s.disputesLogged),
    });

    const promiseTotal = s.promiseToPaySolid + s.promiseToPaySoft;
    rows.push({
      label: COPY.ending.beat5Rows.promises,
      value: String(promiseTotal),
      ok: promiseTotal === 0 ? 'none' : s.promiseToPaySolid > 0 ? 'yes' : 'no',
      okCaption: COPY.ending.beat5Rows.promisesDetail,
      okDetail: `${s.promiseToPaySolid} solid · ${s.promiseToPaySoft} soft`,
    });

    const writeOffTotal = s.writeOffsApproved + s.writeOffsDeclined;
    rows.push({
      label: COPY.ending.beat5Rows.writeOffs,
      value: String(writeOffTotal),
      ok: writeOffTotal === 0 ? 'none' : 'yes',
      okCaption: COPY.ending.beat5Rows.writeOffsDetail,
      okDetail: `${s.writeOffsApproved} approved · ${s.writeOffsDeclined} held`,
    });

    rows.push({
      label: COPY.ending.beat5Rows.unapplied,
      value: `$${s.unappliedAtCloseUsd.toLocaleString()}`,
    });

    if (s.paymentsExpired > 0) {
      rows.push({
        label: COPY.ending.beat5Rows.paymentsExpired,
        value: String(s.paymentsExpired),
        ok: 'no',
        okCaption: COPY.ending.beat5Rows.paymentsExpiredDetail,
      });
    }

    const totalEmailsActioned = s.emailsActionedCorrectly + s.emailsActionedWrong;
    if (totalEmailsActioned > 0) {
      rows.push({
        label: COPY.ending.beat5Rows.emailsActioned,
        value: String(totalEmailsActioned),
        ok: s.emailsActionedCorrectly === totalEmailsActioned ? 'yes' : 'no',
        okCaption: COPY.ending.beat5Rows.emailsActionedDetail,
        okDetail: `${s.emailsActionedCorrectly} / ${totalEmailsActioned}`,
      });
    }

    rows.push({
      label: COPY.ending.beat5Rows.liveScore,
      value: s.liveScore >= 0 ? `+${s.liveScore}` : String(s.liveScore),
      ok: s.liveScore >= 30 ? 'yes' : s.liveScore <= -10 ? 'no' : 'none',
    });

    rows.push({
      label: COPY.ending.beat5Rows.dso,
      value:
        s.dsoAtClose === null
          ? COPY.ending.beat5Rows.noneMarker
          : `${s.dsoAtClose} ${COPY.ending.beat5Rows.dsoSuffix}`,
    });

    return rows;
  }

  private renderScorecardRow(row: ScorecardRow, y: number): Phaser.GameObjects.Text[] {
    const made: Phaser.GameObjects.Text[] = [];

    made.push(this.add.text(SCORECARD.rowLabelX, y, row.label, {
      fontFamily: FONTS.ui,
      fontSize: '18px',
      color: '#ffffff',
    }).setOrigin(0, 0.5));

    made.push(this.add.text(SCORECARD.rowValueX, y, row.value, {
      fontFamily: FONTS.ui,
      fontSize: '18px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    if (row.ok === 'yes') {
      made.push(this.add.text(SCORECARD.rowOkX, y, `${row.okCaption ?? ''} ✓`, {
        fontFamily: FONTS.ui,
        fontSize: '16px',
        color: toHexColor(COLORS.success ?? COLORS.textPrimary),
      }).setOrigin(0, 0.5));
    } else if (row.ok === 'no') {
      const detail = row.okDetail ? `  (${row.okDetail})` : '';
      made.push(this.add.text(SCORECARD.rowOkX, y, `${row.okCaption ?? ''} ✗${detail}`, {
        fontFamily: FONTS.ui,
        fontSize: '16px',
        color: toHexColor(COLORS.warning ?? COLORS.textMuted),
      }).setOrigin(0, 0.5));
    } else if (row.ok === 'none') {
      made.push(this.add.text(SCORECARD.rowOkX, y, COPY.ending.beat5Rows.noneMarker, {
        fontFamily: FONTS.ui,
        fontSize: '16px',
        color: toHexColor(COLORS.textMuted),
      }).setOrigin(0, 0.5));
    }

    return made;
  }

  /** Beat 6: the Fazeshift punchline — leads into the vocab quiz. */
  private renderBeat6(): void {
    const minutes = Math.max(1, Math.round(this.elapsedMs / 60_000));
    const line1 = COPY.ending.punchLine1.replace('{minutes}', String(minutes));

    const line1Text = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, line1, {
      fontFamily: FONTS.ui,
      fontSize: '26px',
      color: '#ffffff',
    }).setOrigin(0.5).setAlpha(0).setDepth(DEPTHS.ending + 4);

    const line2Text = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 20, COPY.ending.punchLine2, {
      fontFamily: FONTS.ui,
      fontSize: '30px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5).setAlpha(0).setDepth(DEPTHS.ending + 4);

    const advance = (): void => {
      this.tweens.add({
        targets: [line1Text, line2Text],
        alpha: 0,
        duration: 300,
        onComplete: () => {
          line1Text.destroy();
          line2Text.destroy();
          this.renderBeat7Quiz();
        },
      });
    };

    this.tweens.add({ targets: line1Text, alpha: 1, duration: BEAT6_LINE1_DURATION });
    this.tweens.add({ targets: line2Text, alpha: 1, duration: BEAT6_LINE2_DURATION, delay: BEAT6_LINE2_DELAY });
    this.time.delayedCall(BEAT6_BUTTON_DELAY + 1500, advance);
    this.input.once('pointerdown', advance);
  }

  /** Beat 7: vocab quiz — 8 questions, one per core term. */
  private renderBeat7Quiz(): void {
    let questionIdx = 0;
    let correct = 0;
    const objects: Phaser.GameObjects.GameObject[] = [];

    const renderQuestion = (): void => {
      objects.forEach((o) => o.destroy());
      objects.length = 0;

      if (questionIdx >= VOCAB_QUIZ.length) {
        this.quizScore = correct;
        this.renderBeat8Share();
        return;
      }

      const q = VOCAB_QUIZ[questionIdx];
      const progress = this.add.text(GAME_WIDTH / 2, 80, `${questionIdx + 1} / ${VOCAB_QUIZ.length}`, {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(COLORS.textMuted),
      }).setOrigin(0.5).setDepth(DEPTHS.ending + 4);
      objects.push(progress);

      const title = this.add.text(GAME_WIDTH / 2, 150, q.prompt, {
        fontFamily: FONTS.ui,
        fontSize: '20px',
        color: '#ffffff',
        wordWrap: { width: GAME_WIDTH - 160 },
        align: 'center',
      }).setOrigin(0.5).setDepth(DEPTHS.ending + 4);
      objects.push(title);

      const skip = this.add.text(GAME_WIDTH - 24, GAME_HEIGHT - 24, COPY.ending.quizSkip, {
        fontFamily: FONTS.ui,
        fontSize: '12px',
        color: toHexColor(COLORS.textMuted),
      }).setOrigin(1, 1).setDepth(DEPTHS.ending + 4).setInteractive({ useHandCursor: true });
      skip.on('pointerdown', () => {
        objects.forEach((o) => o.destroy());
        this.quizScore = correct;
        this.renderBeat8Share();
      });
      objects.push(skip);

      q.options.forEach((option, i) => {
        const y = 240 + i * 64;
        const bg = this.add.rectangle(GAME_WIDTH / 2, y, GAME_WIDTH - 280, 52, COLORS.windowChrome)
          .setStrokeStyle(1, COLORS.windowBorder)
          .setInteractive({ useHandCursor: true })
          .setDepth(DEPTHS.ending + 4);
        const text = this.add.text(GAME_WIDTH / 2, y, option, {
          fontFamily: FONTS.ui,
          fontSize: '14px',
          color: toHexColor(COLORS.textPrimary),
          wordWrap: { width: GAME_WIDTH - 320 },
          align: 'center',
        }).setOrigin(0.5).setDepth(DEPTHS.ending + 5);
        objects.push(bg, text);

        bg.on('pointerdown', () => {
          if (i === q.correctIndex) {
            correct += 1;
            bg.fillColor = COLORS.success;
          } else {
            bg.fillColor = COLORS.warning;
          }
          this.time.delayedCall(450, () => {
            questionIdx += 1;
            renderQuestion();
          });
        });
      });
    };

    renderQuestion();
  }

  /** Beat 8: share card — copy text + download image. */
  private renderBeat8Share(): void {
    const objects: Phaser.GameObjects.GameObject[] = [];
    const shareData = buildShareData(this.scorecard, this.emails.length, 0);
    const shareText = renderShareText(shareData);

    // Send analytics ping.
    sendAnalytics({
      run_id: generateRunId(),
      mode: 'core',
      ended_at: new Date().toISOString(),
      elapsed_real_seconds: Math.round(this.elapsedMs / 1000),
      completed: true,
      craft_score: shareData.craftScore,
      quiz_score: this.quizScore ?? undefined,
      shared: false,
      dso: shareData.dso ?? undefined,
    });

    const title = this.add.text(GAME_WIDTH / 2, 100, COPY.ending.shareTitle, {
      fontFamily: FONTS.ui,
      fontSize: '24px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(DEPTHS.ending + 4);
    objects.push(title);

    if (this.quizScore !== null) {
      const quizScoreText = this.add.text(
        GAME_WIDTH / 2,
        140,
        COPY.ending.quizScoreFormat.replace('{score}', String(this.quizScore)),
        {
          fontFamily: FONTS.ui,
          fontSize: '15px',
          color: toHexColor(COLORS.textMuted),
        },
      ).setOrigin(0.5).setDepth(DEPTHS.ending + 4);
      objects.push(quizScoreText);
    }

    // Show the share text inside a panel.
    const panelW = GAME_WIDTH - 240;
    const panelH = 240;
    const panelX = (GAME_WIDTH - panelW) / 2;
    const panelY = 180;
    const panel = this.add.rectangle(panelX, panelY, panelW, panelH, COLORS.windowBody)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setDepth(DEPTHS.ending + 4);
    objects.push(panel);

    const text = this.add.text(panelX + 16, panelY + 16, shareText, {
      fontFamily: FONTS.mono,
      fontSize: '12px',
      color: toHexColor(COLORS.textPrimary),
      wordWrap: { width: panelW - 32, useAdvancedWrap: true },
    }).setOrigin(0, 0).setDepth(DEPTHS.ending + 5);
    objects.push(text);

    const buttonY = panelY + panelH + 24;
    const copyButton = this.add.text(GAME_WIDTH / 2 - 220, buttonY, COPY.ending.shareCopy, {
      fontFamily: FONTS.ui,
      fontSize: '14px',
      color: toHexColor(COLORS.buttonPrimaryText),
      backgroundColor: toHexColor(COLORS.buttonPrimary),
      padding: { x: 16, y: 10 },
    }).setOrigin(0.5).setDepth(DEPTHS.ending + 5).setInteractive({ useHandCursor: true });
    copyButton.on('pointerdown', () => {
      void copyShareText(shareText).then((ok) => {
        if (ok) copyButton.setText(COPY.ending.shareCopied);
      });
    });
    objects.push(copyButton);

    const downloadButton = this.add.text(GAME_WIDTH / 2, buttonY, COPY.ending.shareDownload, {
      fontFamily: FONTS.ui,
      fontSize: '14px',
      color: toHexColor(COLORS.buttonPrimaryText),
      backgroundColor: toHexColor(COLORS.buttonPrimary),
      padding: { x: 16, y: 10 },
    }).setOrigin(0.5).setDepth(DEPTHS.ending + 5).setInteractive({ useHandCursor: true });
    downloadButton.on('pointerdown', () => downloadShareCardImage(shareData));
    objects.push(downloadButton);

    const doneButton = this.add.text(GAME_WIDTH / 2 + 220, buttonY, COPY.ending.shareDone, {
      fontFamily: FONTS.ui,
      fontSize: '14px',
      color: toHexColor(COLORS.textMuted),
      backgroundColor: toHexColor(COLORS.windowChrome),
      padding: { x: 16, y: 10 },
    }).setOrigin(0.5).setDepth(DEPTHS.ending + 5).setInteractive({ useHandCursor: true });
    doneButton.on('pointerdown', () => {
      AudioManager.getInstance().playButtonClick();
      this.scene.start('MenuScene');
    });
    objects.push(doneButton);
  }
}
